import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '../components/SiteNavbar';
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); obs.unobserve(el); } },
      { threshold: 0.12, ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, isVisible];
};

const LandingPage = () => {
  const [expandedProject, setExpandedProject] = useState(null);
  const [methodFilter, setMethodFilter] = useState('All');

  // Optional: set to the lab's contact email to show an email button in the
  // "Work with us" section. Left empty, only the address block is shown.
  const CONTACT_EMAIL = '';

  const projects = [
    {
      id: 12,
      method: "Optimisation",
      methodGroup: "Operational Planning",
      title: "Port Rail Scheduling",
      subtitle: "Optimisation of Rail Time Windows",
      description: "UNSW and NSW Ports partnership research project",
      fullDescription: "UNSW and NSW Ports partnership research project",
      image: "/Port_Rail.png",
      status: "live",
      link: "/app/port-rail-scheduling",
      isExternal: false,
      tags: [],
      team: [],
      timeframe: "",
      stakeholders: "",
      publications: []
    },
    {
      id: 11,
      method: "Optimisation",
      methodGroup: "Strategic Planning",
      title: "Battery Electric Truck Charging/Swapping Infrastructure Planning",
      subtitle: "Charging and Battery-Swap Station Build-Out Planning for Electric Trucks",
      description: "An interactive map-based planning tool for staging the build-out of charging and battery-swapping stations for battery electric trucks across the freight network.",
      fullDescription: "This tool supports strategic planning of charging and battery-swap infrastructure for battery electric trucks. Users can adjust global model parameters and modelling assumptions to test build-out scenarios, and review candidate station locations, staging, and results on an interactive map.",
      image: "/batterytruckThumbnail.png",
      status: "live",
      link: "/app/battery-truck-charging",
      isExternal: false,
      tags: ["Battery Electric Trucks", "Charging Infrastructure", "Battery Swapping", "Network Planning"],
      team: ["Dr Elnaz Irannezhad", "Dr Jia Guo"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: ["Irannezhad, E., Guo, J., Hours-of-service-aware siting of charging and battery-swapping stations for long-haul electric trucks under adoption uncertainty. https://doi.org/10.48550/arXiv.2610.08187"],
      whitePaper: "/whitepapers/battery-electric-truck.pdf"
    },
    {
      id: 1,
      method: "Optimisation",
      methodGroup: "Strategic Planning",
      title: "E-RailOpt",
      subtitle: "Charging Infrastructure Optimization for Battery Freight Trains",
      description: "A strategic decision-support tool to identify the optimal placement of electric freight rail charging infrastructure. Combines freight train energy consumption modeling with optimization algorithms.",
      fullDescription: "This integrated tool combines the freight train energy consumption model with an optimisation algorithm to determine the optimal number and placement of charging stations along a freight train route. The web-based interface allows decision-makers to modify locomotive and operational parameters, upload route data and candidate sites to evaluate charging station locations.  Watch the presentation here: https://youtu.be/EY7mgdSuXWU",
      image: "/project-erailopt.png",
      status: "live",
      link: "/app",
      isExternal: false,
      tags: ["Optimization", "Energy Modeling", "Battery-Electric Rail", "Decision Support"],
      team: ["Sophia Cibei", "Dr Jia Guo", "Ricardo Alkazzi", "Dr Elnaz Irannezhad"],
      timeframe: "February – September 2025",
      stakeholders: "Transport for NSW",
      publications: [
        "Guo, J. and Irannezhad, E., 2026. Optimizing Freight Rail Electrification: A Framework for Charge Station Selection and Battery Charge/Swap Scheduling. Transportation Research Part E: Logistics and Transportation Review, 105305. https://doi.org/10.1016/j.tre.2026.105305.",
        "Cibei, S, Irannezhad, E., and Guo, J., 2026. E-RailOpt: an Integrated Energy Modelling and Optimisation Tool for Charging Infrastructure Placement in Battery-electric Freight Rail. Nature Portfolio Journal of Sustainable Mobility and Transport, 3, 42 (2026). https://doi.org/10.1038/s44333-026-00108-1."
      ],
      whitePaper: "/whitepapers/e-railopt.pdf"
    },
    {
      id: 2,
      method: "Agent-based simulation",
      methodGroup: "Operational Planning",
      title: "Container Terminal Simulator",
      subtitle: "Agent-Based Model for Terminal Operations",
      description: "An agent-based simulation model evaluating container terminal operations under truck backhauling and multi-trailer truck mechanisms using real-world Port Botany data.",
      fullDescription: "This research addresses a critical gap in understanding container terminal operations under two cooperation mechanisms: truck backhauling and the use of high-productivity multi-trailer trucks (B-double and A-double) at maritime container terminals. Uses four-month real-world data from Port Botany, Sydney. Watch the presentation here: https://youtu.be/6DqHOSYOXfU",
      image: "/project-terminal.png",
      status: "live",
      link: "https://cloud.anylogic.com/model/89d9e7e3-7712-43e2-9dee-75ae5bdb3ff2",
      isExternal: true,
      tags: ["Agent-Based Model", "Simulation", "Port Operations", "Logistics"],
      team: ["Rasyid Indra Pratama", "Dr Elnaz Irannezhad"],
      timeframe: "February – September 2024",
      stakeholders: "OneStop & Patrick Terminal Sydney",
      publications: [
        "Pratama, R.I. and Irannezhad, E. Improving Performance of Container Terminal Operations: An Agent-Based Simulation Model. (Under review at Elsevier Journal of International Shipping and Trade)"
      ],
      whitePaper: "/whitepapers/container-terminal.pdf"
    },
    {
      id: 3,
      method: "System Dynamics simulation",
      methodGroup: "Strategic Planning",
      title: "Freight Decarbonisation Tool",
      subtitle: "Strategic Policy Planning for Zero-Emission Freight",
      description: "A strategic policy assessment tool to analyse freight system behaviour under various decarbonisation scenarios and measure policy effectiveness.",
      fullDescription: "This tool assists policymakers in crafting robust policies for accelerating the transition of the heavy vehicle and freight transport industry towards zero tailpipe vehicles. Users can assess policies such as fuel excise tax, truck fuel efficiency, energy carrier price, and emission factors.",
      image: "/project-decarbonisation.png",
      status: "live",
      link: "https://exchange.iseesystems.com/public/elnaz-irannezhad/rciti/index.html#page1",
      isExternal: true,
      tags: ["System Dynamics", "Policy Analysis", "Decarbonisation", "Heavy Vehicles"],
      team: ["Dr Hossein Hosseini", "Dr Elnaz Irannezhad", "Prof David A. Hensher", "Ikhwan Reza"],
      timeframe: "February 2024 – October 2026",
      stakeholders: "UNSW DGFI",
      publications: [
        "Irannezhad, E., Hosseini, H., David. A Hensher, A System Dynamics Model for Zero-emission Heavy Vehicle Transition in Australia. http://arxiv.org/abs/2610.41042",
        "Hosseini, S.H., Irannezhad, E. and Rashidi, T.H., 2025. Exploring the dynamics of the transport system energy consumption in Australia. Case Studies on Transport Policy, 19, p.101334."
      ],
      whitePaper: "/whitepapers/freight-decarbonisation.pdf"
    },
    {
      id: 4,
      method: "System Dynamics simulation",
      methodGroup: "Strategic Planning",
      title: "Modal Shift Planner",
      subtitle: "Road to Rail Freight Policy Tool",
      description: "A strategic policy testing tool for shifting non-bulk containerised freight from road to rail transport using System Dynamics modeling.",
      fullDescription: "Simulates the dynamic interplay of multiple policies to understand key factors influencing mode choice. Results show Australia can achieve emissions reductions up to 1.1 Mt CO2-e p.a. (-10%) when externality charges are internalised. Watch the presentation here: https://youtu.be/-mzdHxFUmCs",
      image: "/project-modalshift.png",
      status: "live",
      link: "https://exchange.iseesystems.com/public/matthew-sin/sd-freight-road-to-rail-full",
      isExternal: true,
      tags: ["System Dynamics", "Modal Shift", "Policy Testing", "Emissions"],
      team: ["Matthew Sin", "Dr Hossein Hosseini", "Dr Elnaz Irannezhad"],
      timeframe: "February – November 2025",
      stakeholders: "UNSW Research",
      publications: [
        "Paper under review - Modal shift policy assessment for Australian freight transport"
      ]
    },
    {
      id: 5,
      method: "Agent-based simulation",
      methodGroup: "Educational",
      title: "Multimodal Logistics Game",
      subtitle: "Educational Game for a Trade-off between Road Only vs. Multimodal Transport",
      description: "A computerised educational game developed in AnyLogic software for engineering students to learn about the differences between road-only versus multimodal transportation (rail + road) for the last-mile logistics in Sydney.",
      fullDescription: "This game was developed as part of a research thesis by Master's student Mora Tao, and was experimented in a pilot study and compared to a board game developed in Netherlands.",
      image: "/game.jpg",
      status: "live",
      link: "https://cloud.anylogic.com/model/cb00c156-e746-42b0-a4c5-ce222c8fa36f",
      isExternal: true,
      tags: ["Agent-based", "Logistics", "Multimodal Transport", "Educational Game"],
      team: ["Mora Tao", "Dr Elnaz Irannezhad"],
      timeframe: "February – November 2025",
      stakeholders: "UNSW Research",
      publications: []
    },
    {
      id: 6,
      method: "Operational Planning",
      methodGroup: "Operational Planning",
      title: "Forklift Planner",
      subtitle: "Rail Container Terminal Forklift Capacity Planning",
      description: "A planning tool for analysing forklift requirements, train service frequency, equipment utilisation, and terminal throughput under different rail container scenarios.",
      fullDescription: "This tool helps evaluate how many heavy and light forklifts are required to support different daily train service levels. It uses observed handling time assumptions and shows utilisation, service reduction impacts, idle time, and fleet capacity requirements.",
      image: "/forklift-planner.png",
      status: "live",
      link: "/app/forklift-planner",
      isExternal: false,
      tags: ["Rail Terminal", "Forklift Planning", "Capacity Analysis", "Operations"],
      team: ["Dr Elnaz Irannezhad"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: []
    },
    {
      id: 7,
      method: "Operational Planning",
      methodGroup: "Operational Planning",
      title: "Rail Mounted Crane Planner",
      subtitle: "Rail Terminal Scenario Planning with Gantry Cranes",
      description: "A rail-mounted crane planning tool for testing terminal train service scenarios, crane capacity, container throughput, and operational feasibility.",
      fullDescription: "This tool models rail terminal capacity using rail-mounted gantry cranes. It evaluates daily train service targets, crane utilisation, container throughput, train capacity, and theoretical feasibility under different operating assumptions.",
      image: "/rail-mounted-crane-planner.png",
      status: "live",
      link: "/app/rail-mounted-crane-planner",
      isExternal: false,
      tags: ["Rail Terminal", "Gantry Crane", "Scenario Planning", "Capacity"],
      team: ["Dr Elnaz Irannezhad"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: []
    },
    {
      id: 8,
      method: "Operational Planning",
      methodGroup: "Operational Planning",
      title: "ARIA · Container Terminal Yard Planner",
      subtitle: "Container Terminal Yard Scenario Planning",
      description: "A yard planner interface designed to manage container terminal bays, RTG crane status, and gate queues.",
      fullDescription: "ARIA is an advanced yard planner interface designed to manage container terminal operations. It features dynamic RTG crane status tracking, gate queue monitoring, and granular stack state management for optimal terminal efficiency.",
      image: "/aria.png",
      status: "live",
      link: "/app/aria-app",
      isExternal: false,
      tags: ["Yard Planner", "Terminal Operations", "Crane Management", "ARIA"],
      team: ["Maryam Safarnezhad", "Dr Elnaz Irannezhad"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: [],
      whitePaper: "/whitepapers/aria.pdf"
    },
    {
      id: 9,
      method: "Operational Planning",
      methodGroup: "Operational Planning",
      title: "Truck Appointment Prediction Tool",
      subtitle: "Port Truck Booking Demand Planning for Vessel Schedules",
      description: "A truck booking demand prediction tool for estimating daily import and export truck booking volumes based on vessel arrival and departure schedules.",
      fullDescription: "This tool estimates expected changes in daily truck booking demand at Port Botany using vessel arrival and departure schedule inputs. It applies ARDL distributed lag model outputs to predict import, export, and stack-run booking effects across relevant days before and after vessel events.",
      image: "/truck-booking-png.png",
      status: "live",
      link: "/app/truck-booking-predictor",
      isExternal: false,
      tags: ["Truck Booking", "Port Botany", "Demand Prediction", "ARDL"],
      team: ["Dr Elnaz Irannezhad"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: []
    },
    {
      id: 10,
      method: "Emission Modelling",
      methodGroup: "Educational",
      title: "HDV Emission Model DSS",
      subtitle: "Decision Support System for Heavy-Duty Vehicle Emission Model Selection",
      description: "A web-based Decision Support System that helps users identify suitable heavy-duty vehicle (HDV) emission models for their specific research, planning, or policy needs.",
      fullDescription: "This web-based Decision Support System (DSS) helps users identify suitable heavy-duty vehicle (HDV) emission models for their specific research, planning, or policy needs.",
      image: "/hdv_screenshot.png",
      status: "live",
      link: "/app/hdv-emission-dss",
      isExternal: false,
      tags: ["Emission Modelling", "Heavy-Duty Vehicles", "Decision Support", "Policy"],
      team: ["Randy Franstiarajah", "Dr Elnaz Irannezhad", "Ruth Fisher"],
      timeframe: "2026",
      stakeholders: "UNSW Research",
      publications: ["Randy Randy Franstiarajah, Ruth Fisher, Elnaz Irannezhad (2026). Which heavy-duty vehicle emission model to select? A systematic review and decision-support system. https://papers.ssrn.com/sol3/papers.cfm?abstract_id=7677498"]
    },
    {
      id: 11,
      method: "Global Critical Mineral Transport Infrastructure Planning",
      methodGroup: "Strategic Planning",
      title: "Global Critical Mineral Transport Infrastructure Planning",
      subtitle: "Value Chain Analysis and Transport Infrastructure Planning for Global Critical Minerals Supply Chains",
      description: "A web-based Decision Support System that is developed as part of a DFAT-CAIR six-month fellowship project focused on value chain analysis of Australia's Critical Minerals’.",
      fullDescription: "This web-based Decision Support System (DSS) provides a comprehensive, resilient, and sustainable supply chain planning framework linking Australian mineral extraction to global markets and identify the suitability, connectivity, and resilience of transport infrastructure.",
      image: "/criticalmineral.png",
      status: "live",
      link: "/app/critical-minerals-dashboard",
      isExternal: false,
      tags: ["Critical Minerals", "Transport Infrastructure", "Decision Support", "Value Chain Analysis"],
      team: ["Afaq Hussain", "Dr Elnaz Irannezhad"],
      timeframe: "2026-27",
      stakeholders: "UNSW, Department of Foreign Affairs and Trade (DFAT), Centre for Australia-India Relations (CAIR)",
      publications: ["https://www.unsw.edu.au/news/2026/10/unsw-welcomes-2026-maitri-fellow-afaq-hussain"]
    }
  ];

  const methodGroups = ['All', ...Array.from(new Set(projects.map(p => p.methodGroup)))];
  const visibleProjects = methodFilter === 'All'
    ? projects
    : projects.filter(p => p.methodGroup === methodFilter);

  const teamMembers = [
    {
      name: "Dr Elnaz (Elli) Irannezhad",
      group: "research",
      role: "FreightLabX Director · Associate Professor",
      photo: "/headshots/dr_elnaz_elli_irannezhad.JPG",
      bio: "Elli is an Associate Professor of Transport Engineering at rCITI, UNSW Sydney. Her research focuses on the automation, digitalisation, optimisation, and decarbonisation of transport and mobility systems, with a particular emphasis on freight transport. She began her professional career in 2005 as a Civil and Road Design Engineer and has accumulated over a decade of industry experience across consulting and government sectors, complemented by an extensive academic and research career.",
    },
    {
      name: "Dr Hossein Hosseini",
      group: "research",
      role: "Research Fellow",
      photo: "/headshots/dr_hossein_hosseini.jpg",
      bio: "Hossein is an early-career researcher with over 14 years of industry experience applying systems thinking and system dynamics modelling to complex socio-technical and socio-environmental challenges. His work spans energy transitions, public health, urban management, and transport. He currently serves as President of the Oceania Chapter of the System Dynamics Society and is a member of the Management Committee of MSSANZ.",
    },
    {
      name: "Dr Jia Guo",
      group: "research",
      role: "Research Associate",
      photo: "/headshots/dr_jia_guo.jpeg",
      bio: "Jia received her Ph.D. in Operations Research and Industrial Engineering from the University of Texas at Austin. Her research spans transportation, network design, healthcare, and power systems, with publications on freight train planning optimisation, vehicle routing, and travel behaviour analysis. She has extensive experience in mathematical modelling, decomposition algorithms, metaheuristics, and machine learning.",
    },
    {
      name: "Ricardo Alkazzi",
      group: "research",
      role: "Software Engineer",
      photo: "/headshots/ricardo_alkazzi.JPG",
      bio: "Ricardo is a Software Engineering Honours graduate with experience spanning virtual reality, data science, quantum computing, digital twins, and high-performance software development. His current interests focus on real-time simulation, GPU-accelerated computing, scalable distributed systems, and next-generation blockchain infrastructure.",
    },
    {
      name: "Afaq Hussain",
      group: "research",
      role: "Maitri Fellow",
      photo: "/headshots/afaq_hussain.JPG",
      bio: "Afaq Hussain is the recipient of the Centre for Australia-India Relations-supported MAITRI Fellowship-2026 at the Research Centre for Integrated Transport Innovation (rCITI), University of New South Wales, Sydney. Afaq Hussain, with over 20 years of experience, is also the Co-founder and Director of the Bureau of Research on Industry and Economic Fundamentals (BRIEF), a New Delhi-based international trade and policy research organisation. His expertise spans logistics, trade facilitation, regional connectivity infrastructure and related regulatory aspects.",
    },
    {
      name: "Maryam Safarnezhad",
      group: "phd",
      role: "PhD Candidate",
      photo: "/headshots/maryam_safarnezhad.png",
      bio: "Maryam's research focuses on container retrieval optimisation and addressing real-world operational constraints in freight systems. She holds a master's degree in industrial engineering and has a strong background in quantitative modelling, supply chain analytics, and mathematical optimisation. Before commencing her PhD, she contributed to projects on freight modal shift and resilient supply chain systems.",
    },
    {
      name: "Fatemeh Hirbod",
      group: "phd",
      role: "PhD Candidate",
      photo: "/headshots/fatemeh_hirbod.jpg",
      bio: "Fatemeh's research focuses on developing a Synchromodal Optimization Framework for Truck Appointment Systems at Container Terminals, with broader interests in operations research, location-allocation, and routing problems. She holds a master's degree in industrial engineering and is passionate about improving efficiency in transport and logistics systems through advanced optimization techniques.",
    },
    {
      name: "Annisa",
      group: "phd",
      role: "PhD Candidate",
      photo: "/headshots/annisa.PNG",
      bio: "Annisa's doctoral work examines the decision-making factors that influence Australian freight operators' adoption of heavy-duty trucks, integrating behavioural modelling and system-level analysis to support the transition toward sustainable freight systems. Before her doctoral studies, she worked as a junior lecturer in construction engineering management and contributed to research on smart mobility and toll road evaluation in Indonesia.",
    },
    {
      name: "Randy Franstiarajah",
      group: "phd",
      role: "PhD Student",
      photo: "/headshots/randy_franstiarajah.jpg",
      bio: "Randy's research focuses on developing emissions models with particular emphasis on heavy-duty transport. He holds a master's degree in Transportation Engineering from Southeast University, China, where his research focused on characterising the impact of driving behaviour on heavy-duty truck emissions.",
    },
    {
      name: "Matthew Sin",
      group: "masters",
      role: "Research Assistant",
      photo: "/headshots/matthew_sin.jpg",
      bio: "Matthew's Honours research focused on developing a System Dynamics model to simulate shifting non-bulk freight from road to rail. Through a planning assessment tool he developed, policymakers can test the impact of four policies in any desired combination. This assists policymakers in making informed decisions for each state/territory to help address the root cause of anthropogenic climate change by reducing emissions within the transport sector. Currently, he is an Infrastructure Consultant at the project advisory firm TSA Riley, where he is focused on project delivery."

    },
    {
      name: "Sophia Cibei",
      group: "masters",
      role: "2025 Honours Graduate",
      photo: "/headshots/sophia_cibei.jpg",
      bio: "Sophia's Honours research focused on developing train energy consumption models and the integrated web-based tool E-RailOpt. She is the recipient of the UNSW Vice-Chancellor's Academic Excellence at the UNSW Sport Award, the Dean of Engineering Outstanding Undergraduate Award, and the ITE-NZ Undergraduate Award.",
    },
    {
      name: "Ikhwan Reza",
      group: "masters",
      role: "Research Assistant",
      photo: "/headshots/ikhwan_reza.jpg",
      bio: "Ikhwan holds a Master of International Business from UNSW and has a background in system dynamics and applied modelling. He was involved in freight-related projects at FreightLabX during his postgraduate studies, contributing to the development of a system dynamics model for freight decarbonisation.",
    },
    {
      name: "Mora Tao",
      group: "masters",
      role: "2025 Master's Graduate",
      photo: "/headshots/mora_tao.JPG",
      bio: "Mora graduated with a Master's in Transport Engineering from UNSW. Her research interests include freight operations, intermodal transport, agent-based simulation, and game-based learning in logistics. She is also an AnyLogic simulation modeller and a former ocean export coordinator.",
    },
    {
      name: "Rasyid Indra Pratama",
      group: "masters",
      role: "2024 Master's Graduate",
      photo: "/headshots/rasyid_indra_pratama.jpg",
      bio: "Rasyid's master's research involved developing an agent-based simulation model of container terminal operations at Port Botany, examining the impacts of multi-trailer truck utilisation and backhauling mechanisms on terminal performance. He currently serves as a Senior Officer for Maritime Transport at the ASEAN Secretariat.",
    },
  ];

  const TeamMemberCard = ({ member, index }) => {
    const [ref, isVisible] = useReveal();
    const initials = member.name
      .replace(/^Dr\s+/, '')
      .replace(/\(.*?\)\s*/, '')
      .split(' ')
      .map(w => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

    return (
      <div
        ref={ref}
        className="bg-gray-900/60 border border-gray-800 rounded-2xl p-7 transition-colors duration-300 hover:border-gray-700"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
          transition: `opacity 0.6s cubic-bezier(0.4,0,0.2,1) ${index * 0.07}s, transform 0.6s cubic-bezier(0.4,0,0.2,1) ${index * 0.07}s`,
        }}
      >
        <div className="mb-5">
          {member.photo ? (
            <img
              src={member.photo}
              alt={member.name}
              className="w-28 h-28 rounded-2xl object-cover border-2 border-gray-700"
            />
          ) : (
            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-unsw-yellow to-yellow-600 flex items-center justify-center text-2xl font-bold text-black border-2 border-gray-700">
              {initials}
            </div>
          )}
        </div>

        <h3 className="text-white font-semibold text-lg leading-snug">{member.name}</h3>
        <p className="text-unsw-yellow/70 text-sm mt-1 mb-4">{member.role}</p>

        <p className="text-gray-400 text-sm leading-relaxed">{member.bio}</p>
      </div>
    );
  };

  const ProjectCard = ({ project }) => {
    const isExpanded = expandedProject === project.id;

    return (
      <div
        className={`group relative bg-gradient-to-b from-gray-900 to-gray-900/50 rounded-2xl border transition-all duration-500 overflow-hidden ${isExpanded ? 'border-unsw-yellow shadow-2xl shadow-unsw-yellow/10' : 'border-gray-800 hover:border-unsw-yellow/50'
          }`}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-unsw-yellow/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        <div className="relative p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${project.status === 'live'
              ? 'bg-green-500/10 text-green-400 border border-green-500/30'
              : 'bg-gray-500/10 text-gray-400 border border-gray-500/30'
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${project.status === 'live' ? 'bg-green-400' : 'bg-gray-400'}`}></span>
              {project.status === 'live' ? 'Live' : 'Coming Soon'}
            </span>
            {project.isExternal && (
              <span className="text-xs text-gray-500">External</span>
            )}
          </div>

          <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-unsw-yellow transition-colors">
            {project.title}
          </h3>
          <p className="text-unsw-yellow/80 text-sm font-medium mb-3">{project.subtitle}</p>
          {project.method && (
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-4">
              Method: <span className="text-gray-300 normal-case tracking-normal text-sm">{project.method}</span>
            </p>
          )}

          <p className="text-gray-400 mb-6 leading-relaxed">
            {isExpanded ? project.fullDescription : project.description}
          </p>

          <div className="mb-6 rounded-xl overflow-hidden border border-gray-800 group-hover:border-gray-700 transition-colors">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {project.tags.map((tag, i) => (
              <span key={i} className="text-xs bg-gray-800 text-gray-400 px-3 py-1.5 rounded-full border border-gray-700">
                {tag}
              </span>
            ))}
          </div>

          <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="border-t border-gray-800 pt-6 mt-2 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Project Team</h4>
                <p className="text-gray-400 text-sm">{project.team.join(', ')}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Timeframe</h4>
                <p className="text-gray-400 text-sm">{project.timeframe}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Stakeholders</h4>
                <p className="text-gray-400 text-sm">{project.stakeholders}</p>
              </div>
              {project.publications && project.publications.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Publications</h4>
                  <ul className="space-y-2">
                    {project.publications.map((pub, i) => (
                      <li key={i} className="text-gray-400 text-sm pl-4 border-l-2 border-unsw-yellow/30">
                        {pub}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-6 border-t border-gray-800">
            <button
              onClick={() => setExpandedProject(isExpanded ? null : project.id)}
              className="text-gray-400 hover:text-white text-sm flex items-center gap-2 transition-colors"
            >
              {isExpanded ? 'Show Less' : 'Learn More'}
              <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
            {project.whitePaper && (
              <a
                href={project.whitePaper}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-gray-700 text-gray-300 font-semibold px-5 py-2.5 rounded-lg hover:border-unsw-yellow hover:text-unsw-yellow transition-all no-underline"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                White Paper
              </a>
            )}

            {project.isExternal ? (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-unsw-yellow text-black font-semibold px-5 py-2.5 rounded-lg hover:bg-yellow-400 transition-all hover:scale-105"
              >
                Explore the model
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ) : (
              <Link
                to={project.link}
                className="inline-flex items-center gap-2 bg-unsw-yellow text-black font-semibold px-5 py-2.5 rounded-lg hover:bg-yellow-400 transition-all hover:scale-105"
              >
                Explore the model
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      <SiteNavbar />
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(#FFE600 1px, transparent 1px), linear-gradient(90deg, #FFE600 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-unsw-yellow/10 rounded-full blur-[150px]"></div>

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-unsw-yellow/10 border border-unsw-yellow/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-unsw-yellow rounded-full animate-pulse"></span>
              <span className="text-unsw-yellow text-sm font-medium">rCITI · UNSW Sydney</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-[1.1]">
              FreightLabX
              <span className="block text-unsw-yellow text-3xl md:text-5xl mt-3 leading-tight">Research, models and decision tools for freight transport</span>
            </h1>

            <p className="text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl">
              FreightLabX, managed by Dr Elnaz (Elli) Irannezhad at UNSW Sydney, develops optimisation,
              simulation and system dynamics models of freight systems — from rail electrification to port
              operations — publishes the research behind them, and turns them into interactive tools that
              industry and government can use.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="#projects" className="inline-flex items-center gap-2 bg-unsw-yellow text-black font-semibold px-8 py-4 rounded-xl hover:bg-yellow-400 transition-all hover:scale-105 hover:shadow-lg hover:shadow-unsw-yellow/20">
                Explore Projects
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>
              <a href="#about" className="inline-flex items-center gap-2 border border-gray-700 text-white font-semibold px-8 py-4 rounded-xl hover:border-gray-500 hover:bg-white/5 transition-all">
                About Our Research
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Research", text: "Peer-reviewed studies on freight decarbonisation, operations and policy." },
              { step: "02", title: "Models", text: "Optimisation, agent-based simulation, system dynamics and econometric models." },
              { step: "03", title: "Tools", text: "Open, interactive decision support built on those models." },
            ].map((s) => (
              <div key={s.step} className="relative bg-gray-900/60 border border-gray-800 rounded-2xl p-7">
                <div className="text-unsw-yellow/60 text-sm font-mono mb-3">{s.step}</div>
                <h3 className="text-white text-2xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: String(projects.length), label: "Models Developed" },
              { value: "10+", label: "Publications" },
              { value: String(teamMembers.length), label: "Researchers" },
              { value: "5", label: "Partners" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-unsw-yellow mb-2">{stat.value}</div>
                <div className="text-gray-500 text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="projects" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Research Projects</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Each project pairs a model with published research and, where possible, an interactive tool.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-12" role="group" aria-label="Filter projects by method">
            {methodGroups.map((g) => (
              <button
                key={g}
                onClick={() => setMethodFilter(g)}
                aria-pressed={methodFilter === g}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${methodFilter === g
                  ? 'bg-unsw-yellow text-black border-unsw-yellow font-semibold'
                  : 'bg-gray-900 text-gray-400 border-gray-700 hover:border-gray-500 hover:text-white'
                  }`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {visibleProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </section>

      <section id="team" className="py-24 bg-gradient-to-b from-gray-900/50 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Research Team</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              The researchers, engineers, and developers behind FreightLabX.
            </p>
          </div>

          {[
            { key: "research", title: "Research Team" },
            { key: "phd", title: "PhD Students" },
            { key: "masters", title: "Master's and Honours Students" },
          ].map((section) => (
            <div key={section.key} className="mb-16 last:mb-0">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-8">{section.title}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {teamMembers
                  .filter((m) => m.group === section.key)
                  .map((m, i) => (
                    <TeamMemberCard key={m.name} member={m} index={i} />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="about" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">About Our Research</h2>
              <p className="text-gray-400 mb-6 leading-relaxed">
                FreightLabX is a research lab within the Research Centre for Integrated Transport
                Innovation (rCITI) at UNSW Sydney. We develop research, models and decision tools to
                address critical challenges in freight transport and logistics.
              </p>
              <p className="text-gray-400 mb-8 leading-relaxed">
                We collaborate with industry partners including Transport for NSW, port operators,
                and government agencies to ensure our research has real-world impact on sustainable
                transportation policy and operations.
              </p>
              <div className="flex items-center gap-6">
                <img src="/UNSW-logo.png" alt="UNSW" className="h-14 w-auto opacity-80" />
                <img src="/unswrciti_logo.jpeg" alt="rCITI" className="h-14 w-auto opacity-80 rounded" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700">
              <h3 className="text-xl font-bold text-white mb-6">Research Focus Areas</h3>
              <ul className="space-y-4">
                {[
                  "Freight rail electrification and charging infrastructure",
                  "Container terminal operations and optimization",
                  "Heavy vehicle decarbonisation policy",
                  "Freight modal shift from road to rail",
                  "System dynamics modeling for transport policy",
                  "Agent-based simulation for logistics",
                  "Battery-electric truck charging and battery-swapping infrastructure",
                  "Port truck booking and appointment systems",
                  "Heavy-vehicle emission modelling",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-unsw-yellow mt-1 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-unsw-yellow/10 via-transparent to-unsw-yellow/10"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="text-4xl font-bold text-white mb-6">Work With Us</h2>
          <p className="text-gray-400 mb-10 text-lg">
            We welcome industry and government partnerships, collaborative research projects,
            and enquiries from prospective PhD and Master's students.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={CONTACT_EMAIL ? `mailto:${CONTACT_EMAIL}` : '#contact'}
              className="inline-flex items-center gap-3 bg-unsw-yellow text-black font-semibold px-10 py-5 rounded-xl hover:bg-yellow-400 transition-all hover:scale-105 hover:shadow-lg hover:shadow-unsw-yellow/20 text-lg"
            >
              Get in touch
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <a
              href="#projects"
              className="inline-flex items-center gap-2 border border-gray-700 text-white font-semibold px-10 py-5 rounded-xl hover:border-gray-500 hover:bg-white/5 transition-all text-lg"
            >
              Browse all models
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-800 py-12 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/UNSW-logo.png" alt="UNSW" className="h-10 w-auto" />
                <span className="text-white font-semibold">FreightLabX</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                FreightLabX · rCITI · UNSW Sydney<br />
                Research Centre for Integrated Transport Innovation, UNSW Sydney, Australia.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#projects" className="text-gray-500 hover:text-unsw-yellow transition-colors">Projects</a></li>
                <li><a href="#team" className="text-gray-500 hover:text-unsw-yellow transition-colors">Team</a></li>
                <li><a href="#about" className="text-gray-500 hover:text-unsw-yellow transition-colors">About</a></li>
              </ul>
            </div>
            <div id="contact">
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <p className="text-gray-500 text-sm">
                Dr Elnaz (Elli) Irannezhad, FreightLabX Director<br />
                {CONTACT_EMAIL && (<><a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-unsw-yellow transition-colors">{CONTACT_EMAIL}</a><br /></>)}
                School of Civil & Environmental Engineering<br />
                UNSW Sydney, NSW 2052<br />
                Australia
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} UNSW Sydney. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-gray-600 text-sm">
              <a href="https://www.unsw.edu.au/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy</a>
              <a href="https://www.unsw.edu.au/copyright-disclaimer" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Copyright &amp; Disclaimer</a>
              <a href="https://www.unsw.edu.au/accessibility" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Accessibility</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
